import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import PlayerPreviewModal from "./PlayerPreviewModal";
import DirectionalText from "@/components/DirectionalText";

const API = process.env.REACT_APP_BACKEND_URL || "";
const B = "#5B0E14";
const GOLD = "#F1E194";
const CREAM = "#FDF6E3";

// ─── Helpers ────────────────────────────────────────────────────────────────

function timeAgo(ts) {
  if (!ts) return "";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return "الآن";
  if (diff < 3600) return `${Math.floor(diff / 60)} د`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} س`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} يوم`;
  return new Date(ts).toLocaleDateString("ar-SA", { month: "short", day: "numeric" });
}

function shortDate(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("ar-SA", { year: "numeric", month: "short", day: "numeric" });
}

function diffLabel(d) {
  return d === 300 ? "سهل" : d === 600 ? "متوسط" : "صعب";
}
function diffColor(d) {
  return d === 300 ? "#16a34a" : d === 600 ? "#d97706" : "#dc2626";
}
function diffBg(d) {
  return d === 300 ? "#dcfce7" : d === 600 ? "#fef3c7" : "#fee2e2";
}
function typeLabel(t) {
  return t === "image" ? "صورة" : t === "audio" ? "صوت" : "نصي";
}
function typeIcon(t) {
  return t === "image" ? "🖼️" : t === "audio" ? "🎵" : "📝";
}
function typeBg(t) {
  return t === "image" ? "#ede9fe" : t === "audio" ? "#dbeafe" : "#f3f4f6";
}
function typeCol(t) {
  return t === "image" ? "#7c3aed" : t === "audio" ? "#2563eb" : "#374151";
}

const ACTION_ICON = {
  "إضافة": "✅", "تعديل": "✏️", "حذف": "🗑️", "رفع": "📤",
  "حل": "✔️", "إرسال": "📧", "تفعيل": "⚡", "هدية": "🎁", "إنشاء": "🔑",
};
function actionIcon(action = "") {
  for (const [key, icon] of Object.entries(ACTION_ICON)) {
    if (action.includes(key)) return icon;
  }
  return "📋";
}

// ─── Shared sub-components ──────────────────────────────────────────────────

function MiniBar({ value, max }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ background: "rgba(91,14,20,.10)", borderRadius: 4, height: 6, width: "100%", overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: B, borderRadius: 4, transition: "width .6s ease" }} />
    </div>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 14, padding: "20px 18px",
      boxShadow: "0 2px 12px rgba(91,14,20,.08)", border: "1px solid rgba(91,14,20,.08)",
      textAlign: "center", flex: 1, minWidth: 110,
    }}>
      <div style={{ fontSize: "1.5rem", marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: "2rem", fontWeight: 900, color: B, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: ".75rem", color: "#888", marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: ".7rem", color: "#aaa", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "4rem", color: B, opacity: 0.5 }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "2rem", marginBottom: 8, animation: "spin 1s linear infinite", display: "inline-block" }}>⏳</div>
        <div>جاري التحميل...</div>
      </div>
    </div>
  );
}

// ─── Question Card ───────────────────────────────────────────────────────────

function QuestionCard({ q, onOpen }) {
  const text = q.text || "";
  const preview = text.length > 80 ? text.slice(0, 80) + "..." : text;

  return (
    <div
      onClick={() => onOpen(q)}
      style={{
        background: "#fff", borderRadius: 14, padding: "16px 18px",
        boxShadow: "0 2px 10px rgba(91,14,20,.07)", border: "1px solid rgba(91,14,20,.08)",
        cursor: "pointer", transition: "all .2s ease",
        display: "flex", flexDirection: "column", gap: 10,
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(91,14,20,.15)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 10px rgba(91,14,20,.07)"; e.currentTarget.style.transform = "none"; }}
    >
      {/* Top row: badges */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{
          background: typeBg(q.question_type), color: typeCol(q.question_type),
          borderRadius: 20, padding: "2px 10px", fontSize: ".72rem", fontWeight: 700,
        }}>
          {typeIcon(q.question_type)} {typeLabel(q.question_type)}
        </span>
        <span style={{
          background: diffBg(q.difficulty), color: diffColor(q.difficulty),
          borderRadius: 20, padding: "2px 10px", fontSize: ".72rem", fontWeight: 700,
        }}>
          {diffLabel(q.difficulty)} ({q.difficulty})
        </span>
        {q.report_count > 0 && (
          <span style={{ background: "#fee2e2", color: "#dc2626", borderRadius: 20, padding: "2px 10px", fontSize: ".72rem", fontWeight: 700 }}>
            ⚠️ {q.report_count} بلاغ
          </span>
        )}
      </div>

      {/* Question text */}
      <div style={{ fontSize: ".9rem", fontWeight: 600, color: "#1f2937", lineHeight: 1.6 }}>
        {preview}
      </div>

      {/* Category + group */}
      <div style={{ fontSize: ".75rem", color: "#6b7280" }}>
        {q.category_name && <span>{q.category_name}</span>}
        {q.group_name && <span style={{ color: "#9ca3af" }}> · {q.group_name}</span>}
      </div>

      {/* Dates */}
      <div style={{ display: "flex", gap: 12, fontSize: ".7rem", color: "#9ca3af", marginTop: 2 }}>
        <span>أُضيف: {shortDate(q.created_at)}</span>
        {q.updated_at && <span>عُدِّل: {shortDate(q.updated_at)}</span>}
      </div>
    </div>
  );
}

// ─── Question Detail Modal ───────────────────────────────────────────────────

function QuestionDetailModal({ q: initialQ, token, adminName, isSuperAdmin, categories, onClose, onUpdate }) {
  const [q, setQ] = useState(initialQ);
  const [history, setHistory] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showPlayerPreview, setShowPlayerPreview] = useState(false);
  const [replaceImg, setReplaceImg] = useState(false);
  const [replaceAudio, setReplaceAudio] = useState(false);
  const [newImgUrl, setNewImgUrl] = useState("");
  const [newAudioUrl, setNewAudioUrl] = useState("");
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [editText, setEditText] = useState(q.text || "");
  const [editAnswer, setEditAnswer] = useState(q.answer || "");
  const [editChoices, setEditChoices] = useState(q.choices || []);
  const [editCategoryId, setEditCategoryId] = useState(q.category_id || "");
  const [editDifficulty, setEditDifficulty] = useState(q.difficulty || 300);
  const [editType, setEditType] = useState(q.question_type || "text");

  const headers = { Authorization: `Bearer ${token}` };
  const canEdit = isSuperAdmin || q.created_by_name === adminName;

  async function loadHistory() {
    if (history !== null) { setHistoryOpen(v => !v); return; }
    try {
      const res = await axios.get(`${API}/api/admin/questions/${q.id}/history`, { headers });
      setHistory(res.data.history || []);
      setHistoryOpen(true);
    } catch {
      toast.error("تعذّر تحميل السجل");
    }
  }

  async function saveEdit() {
    setSaving(true);
    try {
      const body = {
        category_id: editCategoryId,
        difficulty: Number(editDifficulty),
        text: editText,
        answer: editAnswer,
        choices: editChoices,
        image_url: q.image_url || "",
        answer_image_url: q.answer_image_url || "",
        audio_url: q.audio_url || "",
        question_type: editType,
      };
      const res = await axios.put(`${API}/api/questions/${q.id}`, body, { headers });
      setQ({ ...q, ...res.data });
      onUpdate({ ...q, ...res.data });
      setEditMode(false);
      toast.success("تم حفظ التعديلات");
    } catch {
      toast.error("فشل الحفظ");
    } finally {
      setSaving(false);
    }
  }

  async function saveImg() {
    setSaving(true);
    try {
      const body = {
        category_id: q.category_id, difficulty: q.difficulty, text: q.text,
        answer: q.answer, choices: q.choices || [], image_url: newImgUrl,
        answer_image_url: q.answer_image_url || "", audio_url: q.audio_url || "",
        question_type: q.question_type,
      };
      const res = await axios.put(`${API}/api/questions/${q.id}`, body, { headers });
      setQ({ ...q, ...res.data, image_url: newImgUrl });
      onUpdate({ ...q, ...res.data, image_url: newImgUrl });
      setReplaceImg(false);
      setNewImgUrl("");
      toast.success("تم تحديث الصورة");
    } catch {
      toast.error("فشل التحديث");
    } finally {
      setSaving(false);
    }
  }

  async function saveAudio() {
    setSaving(true);
    try {
      const body = {
        category_id: q.category_id, difficulty: q.difficulty, text: q.text,
        answer: q.answer, choices: q.choices || [], image_url: q.image_url || "",
        answer_image_url: q.answer_image_url || "", audio_url: newAudioUrl,
        question_type: q.question_type,
      };
      const res = await axios.put(`${API}/api/questions/${q.id}`, body, { headers });
      setQ({ ...q, ...res.data, audio_url: newAudioUrl });
      onUpdate({ ...q, ...res.data, audio_url: newAudioUrl });
      setReplaceAudio(false);
      setNewAudioUrl("");
      toast.success("تم تحديث الصوت");
    } catch {
      toast.error("فشل التحديث");
    } finally {
      setSaving(false);
    }
  }

  // Close on backdrop click
  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  // Trap escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const infoRow = (label, value) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "10px 0", borderBottom: "1px solid rgba(91,14,20,.06)" }}>
      <span style={{ fontSize: ".7rem", color: "#9ca3af", fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: ".85rem", color: "#1f2937" }}>{value || "—"}</span>
    </div>
  );

  return (
    <>
    {showPlayerPreview && (
      <PlayerPreviewModal
        question={q}
        catName={(categories || []).find(c => c.id === q.category_id)?.name || ""}
        onClose={() => setShowPlayerPreview(false)}
        token={token}
      />
    )}
    <div
      onClick={handleBackdrop}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px", backdropFilter: "blur(3px)",
      }}
    >
      <div
        style={{
          background: "#fff", borderRadius: 20, width: "100%", maxWidth: 720,
          maxHeight: "90vh", overflowY: "auto", direction: "rtl",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)", animation: "slideUp .2s ease",
        }}
      >
        {/* Modal header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "20px 24px 16px", borderBottom: "1px solid rgba(91,14,20,.08)",
          position: "sticky", top: 0, background: "#fff", borderRadius: "20px 20px 0 0", zIndex: 1,
        }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span style={{ background: typeBg(q.question_type), color: typeCol(q.question_type), borderRadius: 20, padding: "4px 12px", fontSize: ".8rem", fontWeight: 700 }}>
              {typeIcon(q.question_type)} {typeLabel(q.question_type)}
            </span>
            <span style={{ background: diffBg(q.difficulty), color: diffColor(q.difficulty), borderRadius: 20, padding: "4px 12px", fontSize: ".8rem", fontWeight: 700 }}>
              {diffLabel(q.difficulty)} ({q.difficulty})
            </span>
            {q.report_count > 0 && (
              <span style={{ background: "#fee2e2", color: "#dc2626", borderRadius: 20, padding: "4px 12px", fontSize: ".8rem", fontWeight: 700 }}>
                ⚠️ {q.report_count} بلاغ
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.4rem", color: "#6b7280", lineHeight: 1, padding: 4 }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: "20px 24px 28px" }}>

          {/* Preview mode */}
          {previewMode && (
            <div style={{ background: CREAM, border: `2px solid ${GOLD}`, borderRadius: 14, padding: 20, marginBottom: 20 }}>
              <div style={{ fontSize: ".7rem", color: "#9ca3af", marginBottom: 12, fontWeight: 600 }}>معاينة كما يراها اللاعب</div>
              <DirectionalText as="div" style={{ fontSize: "1.1rem", fontWeight: 700, color: "#1f2937", marginBottom: 12 }}>{q.text}</DirectionalText>
              {q.image_url && <img src={q.image_url} alt="" style={{ maxWidth: "100%", maxHeight: 180, borderRadius: 10, marginBottom: 12 }} />}
              {q.audio_url && <audio controls src={q.audio_url} style={{ width: "100%", marginBottom: 12 }} />}
              {(q.choices || []).length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {q.choices.map((ch, i) => (
                    <div key={i} style={{
                      padding: "10px 14px", borderRadius: 10,
                      background: ch === q.answer ? "#dcfce7" : "#f9fafb",
                      border: `1.5px solid ${ch === q.answer ? "#16a34a" : "#e5e7eb"}`,
                      fontWeight: ch === q.answer ? 700 : 400,
                      color: ch === q.answer ? "#15803d" : "#374151",
                    }}>
                      {String.fromCharCode(0x0041 + i)}. {ch}
                      {ch === q.answer && <span style={{ marginRight: 8, fontSize: ".8rem" }}>✓</span>}
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setPreviewMode(false)} style={{
                marginTop: 12, padding: "6px 14px", borderRadius: 8, border: "none",
                background: "rgba(91,14,20,.1)", color: B, cursor: "pointer", fontSize: ".8rem", fontWeight: 700,
              }}>
                إغلاق المعاينة
              </button>
            </div>
          )}

          {/* Edit form */}
          {editMode ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: B, fontSize: "1rem", fontWeight: 800 }}>تعديل السؤال</h3>

              <div>
                <label style={{ fontSize: ".8rem", color: "#6b7280", fontWeight: 600, display: "block", marginBottom: 4 }}>نص السؤال</label>
                <textarea
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  rows={3}
                  style={{ width: "100%", borderRadius: 10, border: "1.5px solid rgba(91,14,20,.15)", padding: "10px 12px", fontSize: ".9rem", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
                />
              </div>

              <div>
                <label style={{ fontSize: ".8rem", color: "#6b7280", fontWeight: 600, display: "block", marginBottom: 4 }}>الإجابة الصحيحة</label>
                <input
                  value={editAnswer}
                  onChange={e => setEditAnswer(e.target.value)}
                  style={{ width: "100%", borderRadius: 10, border: "1.5px solid rgba(91,14,20,.15)", padding: "10px 12px", fontSize: ".9rem", fontFamily: "inherit", boxSizing: "border-box" }}
                />
              </div>

              {editChoices.length > 0 && (
                <div>
                  <label style={{ fontSize: ".8rem", color: "#6b7280", fontWeight: 600, display: "block", marginBottom: 6 }}>الخيارات</label>
                  {editChoices.map((ch, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <span style={{ width: 28, height: 38, display: "flex", alignItems: "center", justifyContent: "center", background: ch === editAnswer ? "#dcfce7" : "#f3f4f6", borderRadius: 8, fontSize: ".8rem", fontWeight: 700, color: ch === editAnswer ? "#16a34a" : "#6b7280", flexShrink: 0 }}>
                        {String.fromCharCode(0x0041 + i)}
                      </span>
                      <input
                        value={ch}
                        onChange={e => {
                          const next = [...editChoices];
                          next[i] = e.target.value;
                          setEditChoices(next);
                        }}
                        style={{ flex: 1, borderRadius: 10, border: `1.5px solid ${ch === editAnswer ? "#16a34a" : "rgba(91,14,20,.15)"}`, padding: "8px 12px", fontSize: ".85rem", fontFamily: "inherit" }}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ fontSize: ".75rem", color: "#6b7280", fontWeight: 600, display: "block", marginBottom: 4 }}>الفئة</label>
                  <select
                    value={editCategoryId}
                    onChange={e => setEditCategoryId(e.target.value)}
                    style={{ width: "100%", borderRadius: 10, border: "1.5px solid rgba(91,14,20,.15)", padding: "8px 10px", fontSize: ".82rem", fontFamily: "inherit" }}
                  >
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: ".75rem", color: "#6b7280", fontWeight: 600, display: "block", marginBottom: 4 }}>الصعوبة</label>
                  <select
                    value={editDifficulty}
                    onChange={e => setEditDifficulty(Number(e.target.value))}
                    style={{ width: "100%", borderRadius: 10, border: "1.5px solid rgba(91,14,20,.15)", padding: "8px 10px", fontSize: ".82rem", fontFamily: "inherit" }}
                  >
                    <option value={300}>سهل (300)</option>
                    <option value={600}>متوسط (600)</option>
                    <option value={900}>صعب (900)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: ".75rem", color: "#6b7280", fontWeight: 600, display: "block", marginBottom: 4 }}>النوع</label>
                  <select
                    value={editType}
                    onChange={e => setEditType(e.target.value)}
                    style={{ width: "100%", borderRadius: 10, border: "1.5px solid rgba(91,14,20,.15)", padding: "8px 10px", fontSize: ".82rem", fontFamily: "inherit" }}
                  >
                    <option value="text">نصي</option>
                    <option value="image">صورة</option>
                    <option value="audio">صوت</option>
                  </select>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", cursor: saving ? "wait" : "pointer", background: B, color: GOLD, fontWeight: 800, fontSize: ".9rem", opacity: saving ? 0.7 : 1 }}
                >
                  {saving ? "جاري الحفظ..." : "💾 حفظ التعديلات"}
                </button>
                <button
                  onClick={() => setEditMode(false)}
                  style={{ padding: "10px 18px", borderRadius: 10, border: "1.5px solid rgba(91,14,20,.15)", cursor: "pointer", background: "#fff", color: "#6b7280", fontWeight: 700, fontSize: ".9rem" }}
                >
                  إلغاء
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Question text */}
              <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#1f2937", lineHeight: 1.7, marginBottom: 16 }}>
                <DirectionalText>{q.text}</DirectionalText>
              </div>

              {/* Image */}
              {q.image_url && (
                <div style={{ marginBottom: 16 }}>
                  <img src={q.image_url} alt="" style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 12, border: "1px solid rgba(91,14,20,.1)" }} />
                </div>
              )}

              {/* Audio */}
              {q.audio_url && (
                <div style={{ marginBottom: 16 }}>
                  <audio controls src={q.audio_url} style={{ width: "100%" }} />
                </div>
              )}

              {/* Choices */}
              {(q.choices || []).length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  <div style={{ fontSize: ".75rem", color: "#9ca3af", fontWeight: 600, marginBottom: 4 }}>الخيارات</div>
                  {q.choices.map((ch, i) => (
                    <div key={i} style={{
                      padding: "10px 14px", borderRadius: 10,
                      background: ch === q.answer ? "#dcfce7" : "#f9fafb",
                      border: `1.5px solid ${ch === q.answer ? "#16a34a" : "#e5e7eb"}`,
                      fontSize: ".88rem", fontWeight: ch === q.answer ? 700 : 400,
                      color: ch === q.answer ? "#15803d" : "#374151",
                      display: "flex", alignItems: "center", gap: 8,
                    }}>
                      <span style={{ fontWeight: 700, opacity: 0.5 }}>{String.fromCharCode(0x0041 + i)}.</span>
                      {ch}
                      {ch === q.answer && <span style={{ marginRight: "auto", fontSize: ".8rem" }}>✓ الإجابة</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Answer box */}
              <div style={{ background: "#dcfce7", border: "1.5px solid #16a34a", borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
                <div style={{ fontSize: ".7rem", color: "#15803d", fontWeight: 600, marginBottom: 4 }}>الإجابة الصحيحة</div>
                <div style={{ fontSize: "1rem", fontWeight: 800, color: "#166534" }}>{q.answer}</div>
              </div>

              {/* Info grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px", marginBottom: 20 }}>
                {infoRow("الفئة", q.category_name)}
                {infoRow("المجموعة", q.group_name)}
                {infoRow("الصعوبة", `${diffLabel(q.difficulty)} (${q.difficulty})`)}
                {infoRow("النوع", typeLabel(q.question_type))}
                {infoRow("أُضيف بواسطة", q.created_by_name)}
                {infoRow("تاريخ الإضافة", shortDate(q.created_at))}
                {infoRow("عُدِّل بواسطة", q.updated_by_name)}
                {infoRow("تاريخ التعديل", shortDate(q.updated_at))}
              </div>
            </>
          )}

          {/* Action buttons */}
          {!editMode && canEdit && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
              <button
                onClick={() => setEditMode(true)}
                style={{ padding: "8px 16px", borderRadius: 10, border: "none", cursor: "pointer", background: B, color: GOLD, fontWeight: 700, fontSize: ".82rem" }}
              >
                ✏️ تعديل السؤال
              </button>
              <button
                onClick={() => setShowPlayerPreview(true)}
                style={{ padding: "8px 16px", borderRadius: 10, border: "none", cursor: "pointer", background: "rgba(30,27,100,.10)", color: "#1e1b4b", fontWeight: 700, fontSize: ".82rem", display:"flex", alignItems:"center", gap:5 }}
              >
                👁 معاينة اللاعب
              </button>
              <button
                onClick={() => setReplaceImg(v => !v)}
                style={{ padding: "8px 16px", borderRadius: 10, border: "none", cursor: "pointer", background: "#ede9fe", color: "#7c3aed", fontWeight: 700, fontSize: ".82rem" }}
              >
                🖼️ استبدال الصورة
              </button>
              <button
                onClick={() => setReplaceAudio(v => !v)}
                style={{ padding: "8px 16px", borderRadius: 10, border: "none", cursor: "pointer", background: "#dbeafe", color: "#2563eb", fontWeight: 700, fontSize: ".82rem" }}
              >
                🎵 استبدال الصوت
              </button>
            </div>
          )}

          {/* Replace image form */}
          {replaceImg && (
            <div style={{ background: "#faf5ff", border: "1px solid #ddd6fe", borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: ".8rem", fontWeight: 700, color: "#7c3aed", marginBottom: 8 }}>رابط الصورة الجديدة</div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={newImgUrl}
                  onChange={e => setNewImgUrl(e.target.value)}
                  placeholder="https://..."
                  style={{ flex: 1, borderRadius: 8, border: "1px solid #ddd6fe", padding: "8px 12px", fontSize: ".85rem" }}
                />
                <button onClick={saveImg} disabled={saving || !newImgUrl} style={{ padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer", background: "#7c3aed", color: "#fff", fontWeight: 700, fontSize: ".82rem", opacity: (!newImgUrl || saving) ? 0.5 : 1 }}>
                  حفظ
                </button>
              </div>
            </div>
          )}

          {/* Replace audio form */}
          {replaceAudio && (
            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: ".8rem", fontWeight: 700, color: "#2563eb", marginBottom: 8 }}>رابط الصوت الجديد</div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={newAudioUrl}
                  onChange={e => setNewAudioUrl(e.target.value)}
                  placeholder="https://..."
                  style={{ flex: 1, borderRadius: 8, border: "1px solid #bfdbfe", padding: "8px 12px", fontSize: ".85rem" }}
                />
                <button onClick={saveAudio} disabled={saving || !newAudioUrl} style={{ padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer", background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: ".82rem", opacity: (!newAudioUrl || saving) ? 0.5 : 1 }}>
                  حفظ
                </button>
              </div>
            </div>
          )}

          {/* Edit history */}
          <div>
            <button
              onClick={loadHistory}
              style={{
                width: "100%", padding: "10px 16px", borderRadius: 10,
                border: "1.5px solid rgba(91,14,20,.12)", cursor: "pointer",
                background: historyOpen ? CREAM : "#fff", color: B,
                fontWeight: 700, fontSize: ".85rem", textAlign: "right",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}
            >
              <span>📋 سجل التعديلات</span>
              <span style={{ fontSize: ".75rem", opacity: 0.6 }}>{historyOpen ? "▲" : "▼"}</span>
            </button>

            {historyOpen && history !== null && (
              <div style={{ background: CREAM, borderRadius: "0 0 10px 10px", border: "1.5px solid rgba(91,14,20,.12)", borderTop: "none", padding: 12 }}>
                {history.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "1rem", color: "#9ca3af", fontSize: ".82rem" }}>لا توجد تعديلات مسجّلة</div>
                ) : (
                  history.map((h, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: i < history.length - 1 ? "1px solid rgba(91,14,20,.07)" : "none" }}>
                      <span style={{ fontSize: "1rem" }}>{actionIcon(h.action)}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: ".82rem", fontWeight: 700, color: "#374151" }}>{h.action} — {h.admin_name}</div>
                        {h.details && <div style={{ fontSize: ".72rem", color: "#9ca3af", marginTop: 2 }}>{h.details}</div>}
                      </div>
                      <span style={{ fontSize: ".7rem", color: "#9ca3af", whiteSpace: "nowrap" }}>{timeAgo(h.timestamp)}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
    </>
  );
}

// ─── Questions Tab ───────────────────────────────────────────────────────────

function QuestionsTab({ token, adminName, isSuperAdmin, categories }) {
  const [questions, setQuestions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, per_page: 20 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [difficulty, setDifficulty] = useState(0);
  const [questionType, setQuestionType] = useState("");
  const [sort, setSort] = useState("created_at");
  const [selectedQ, setSelectedQ] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, per_page: 20,
        ...(search && { search }),
        ...(categoryId && { category_id: categoryId }),
        ...(difficulty && { difficulty }),
        ...(questionType && { question_type: questionType }),
        sort,
      });
      const res = await axios.get(`${API}/api/admin/my-questions?${params}`, { headers });
      setQuestions(res.data.questions || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      toast.error("تعذّر تحميل الأسئلة");
    } finally {
      setLoading(false);
    }
  }, [page, search, categoryId, difficulty, questionType, sort, token]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, categoryId, difficulty, questionType, sort]);

  function handleUpdate(updatedQ) {
    setQuestions(qs => qs.map(q => q.id === updatedQ.id ? { ...q, ...updatedQ } : q));
    setSelectedQ(prev => prev && prev.id === updatedQ.id ? { ...prev, ...updatedQ } : prev);
  }

  const filterStyle = {
    borderRadius: 10, border: "1.5px solid rgba(91,14,20,.12)",
    padding: "8px 12px", fontSize: ".82rem", fontFamily: "inherit",
    background: "#fff", color: "#374151", outline: "none",
    transition: "border-color .2s",
  };

  return (
    <div>
      {/* Filters */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 بحث في الأسئلة..."
          style={{ ...filterStyle, flex: "1 1 180px", minWidth: 140 }}
        />
        <select value={categoryId} onChange={e => setCategoryId(e.target.value)} style={{ ...filterStyle, flex: "0 1 160px" }}>
          <option value="">كل الفئات</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={difficulty} onChange={e => setDifficulty(Number(e.target.value))} style={{ ...filterStyle, flex: "0 1 130px" }}>
          <option value={0}>كل الصعوبات</option>
          <option value={300}>سهل (300)</option>
          <option value={600}>متوسط (600)</option>
          <option value={900}>صعب (900)</option>
        </select>
        <select value={questionType} onChange={e => setQuestionType(e.target.value)} style={{ ...filterStyle, flex: "0 1 120px" }}>
          <option value="">كل الأنواع</option>
          <option value="text">نصي</option>
          <option value="image">صورة</option>
          <option value="audio">صوت</option>
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)} style={{ ...filterStyle, flex: "0 1 140px" }}>
          <option value="created_at">الأحدث</option>
          <option value="updated_at">آخر تعديل</option>
        </select>
      </div>

      {/* Count */}
      {!loading && (
        <div style={{ fontSize: ".8rem", color: "#9ca3af", marginBottom: 16 }}>
          {pagination.total} سؤال إجمالاً
        </div>
      )}

      {/* Grid */}
      {loading ? <Spinner /> : questions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "#9ca3af" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>📭</div>
          <div>لا توجد أسئلة مطابقة</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14, marginBottom: 24 }}>
          {questions.map(q => (
            <QuestionCard key={q.id} q={q} onOpen={setSelectedQ} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 8 }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ padding: "7px 16px", borderRadius: 8, border: "none", cursor: page === 1 ? "default" : "pointer", background: "rgba(91,14,20,.1)", color: B, opacity: page === 1 ? 0.4 : 1, fontWeight: 700 }}
          >
            السابق
          </button>
          <span style={{ fontSize: ".85rem", color: "#6b7280" }}>
            صفحة {page} من {pagination.pages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
            disabled={page === pagination.pages}
            style={{ padding: "7px 16px", borderRadius: 8, border: "none", cursor: page === pagination.pages ? "default" : "pointer", background: "rgba(91,14,20,.1)", color: B, opacity: page === pagination.pages ? 0.4 : 1, fontWeight: 700 }}
          >
            التالي
          </button>
        </div>
      )}

      {/* Modal */}
      {selectedQ && (
        <QuestionDetailModal
          q={selectedQ}
          token={token}
          adminName={adminName}
          isSuperAdmin={isSuperAdmin}
          categories={categories}
          onClose={() => setSelectedQ(null)}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
}

// ─── Stats Tab ───────────────────────────────────────────────────────────────

function StatsTab({ token }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/admin/my-questions/stats`, { headers });
        setStats(res.data);
      } catch {
        toast.error("تعذّر تحميل الإحصائيات");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) return <Spinner />;
  if (!stats) return <div style={{ textAlign: "center", padding: "3rem", color: "#9ca3af" }}>لا توجد بيانات</div>;

  const maxCat = stats.category_breakdown.length > 0 ? stats.category_breakdown[0].count : 1;

  const typeOrder = [
    { key: "text", label: "نصي", icon: "📝", bg: "#f3f4f6", col: "#374151" },
    { key: "image", label: "صورة", icon: "🖼️", bg: "#ede9fe", col: "#7c3aed" },
    { key: "audio", label: "صوت", icon: "🎵", bg: "#dbeafe", col: "#2563eb" },
  ];

  const diffOrder = [
    { key: "300", label: "سهل", col: "#16a34a", bg: "#dcfce7" },
    { key: "600", label: "متوسط", col: "#d97706", bg: "#fef3c7" },
    { key: "900", label: "صعب", col: "#dc2626", bg: "#fee2e2" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Top stat cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <StatCard icon="📝" label="إجمالي أسئلتي" value={stats.total_questions.toLocaleString("ar")} />
        <StatCard icon="⚠️" label="إجمالي البلاغات" value={stats.total_reports.toLocaleString("ar")} />
        <StatCard icon="🗓️" label="أول مساهمة" value={stats.first_contribution_at ? shortDate(stats.first_contribution_at) : "—"} />
      </div>

      {/* Category breakdown */}
      {stats.category_breakdown.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 14, padding: "20px 20px", boxShadow: "0 2px 12px rgba(91,14,20,.08)", border: "1px solid rgba(91,14,20,.08)" }}>
          <h3 style={{ margin: "0 0 16px", color: B, fontSize: ".9rem", fontWeight: 800 }}>توزيع الأسئلة حسب الفئة</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {stats.category_breakdown.map(c => (
              <div key={c.id}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: ".83rem", color: "#374151", fontWeight: 600 }}>{c.name || c.id}</span>
                  <span style={{ fontSize: ".83rem", fontWeight: 800, color: B }}>{c.count}</span>
                </div>
                <div style={{ background: "rgba(91,14,20,.08)", borderRadius: 6, height: 8, overflow: "hidden" }}>
                  <div style={{
                    width: `${Math.round((c.count / maxCat) * 100)}%`,
                    height: "100%", background: B, borderRadius: 6,
                    transition: "width .7s ease",
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Type + Difficulty breakdown */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* Type */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "20px 18px", boxShadow: "0 2px 12px rgba(91,14,20,.08)", border: "1px solid rgba(91,14,20,.08)" }}>
          <h3 style={{ margin: "0 0 14px", color: B, fontSize: ".9rem", fontWeight: 800 }}>حسب النوع</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {typeOrder.map(t => (
              <div key={t.key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: t.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", flexShrink: 0 }}>
                  {t.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: ".8rem", color: "#6b7280", fontWeight: 600 }}>{t.label}</div>
                  <div style={{ fontSize: "1.2rem", fontWeight: 900, color: t.col }}>{(stats.type_breakdown[t.key] || 0).toLocaleString("ar")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Difficulty */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "20px 18px", boxShadow: "0 2px 12px rgba(91,14,20,.08)", border: "1px solid rgba(91,14,20,.08)" }}>
          <h3 style={{ margin: "0 0 14px", color: B, fontSize: ".9rem", fontWeight: 800 }}>حسب الصعوبة</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {diffOrder.map(d => (
              <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: d.bg, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: ".85rem", color: d.col, flexShrink: 0 }}>
                  {d.key}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: ".8rem", color: "#6b7280", fontWeight: 600 }}>{d.label}</div>
                  <div style={{ fontSize: "1.2rem", fontWeight: 900, color: d.col }}>{(stats.difficulty_breakdown[d.key] || 0).toLocaleString("ar")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Most reported question */}
      {stats.most_reported_question && (
        <div style={{ background: "#fff5f5", border: "1.5px solid #fecaca", borderRadius: 14, padding: "18px 20px", boxShadow: "0 2px 12px rgba(91,14,20,.05)" }}>
          <h3 style={{ margin: "0 0 10px", color: "#dc2626", fontSize: ".9rem", fontWeight: 800 }}>⚠️ الأكثر إبلاغاً</h3>
          <DirectionalText as="div" style={{ fontSize: ".9rem", color: "#374151", marginBottom: 6 }}>{stats.most_reported_question.text}</DirectionalText>
          <div style={{ fontSize: ".8rem", color: "#dc2626", fontWeight: 700 }}>{stats.most_reported_question.reports} بلاغ</div>
        </div>
      )}

      {/* Last edits */}
      {stats.last_edits.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 14, padding: "20px 20px", boxShadow: "0 2px 12px rgba(91,14,20,.08)", border: "1px solid rgba(91,14,20,.08)" }}>
          <h3 style={{ margin: "0 0 14px", color: B, fontSize: ".9rem", fontWeight: 800 }}>آخر التعديلات</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {stats.last_edits.map((e, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "10px 0", borderBottom: i < stats.last_edits.length - 1 ? "1px solid rgba(91,14,20,.06)" : "none",
              }}>
                <span style={{ fontSize: "1rem", marginTop: 1 }}>✏️</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: ".83rem", fontWeight: 600, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {e.target_name || "سؤال"}
                  </div>
                </div>
                <span style={{ fontSize: ".72rem", color: "#9ca3af", whiteSpace: "nowrap" }}>{timeAgo(e.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Log Tab (Activity Log) ───────────────────────────────────────────────────

function LogTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [days, setDays] = useState(30);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/my-activity?days=${days}&page=${page}`, { headers });
      setData(res.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [page, days, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;
  if (!data) return null;

  const { stats, chart, logs, pagination } = data;
  const { breakdown } = stats;
  const maxBar = Math.max(...Object.values(breakdown), 1);
  const maxChart = Math.max(...chart.map(c => c.count), 1);

  return (
    <div>
      {/* Period selector */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, justifyContent: "flex-end" }}>
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => { setDays(d); setPage(1); }} style={{
            padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700,
            fontSize: ".8rem", background: days === d ? B : "rgba(91,14,20,.08)",
            color: days === d ? GOLD : B, transition: "all .2s",
          }}>
            {d === 7 ? "أسبوع" : d === 30 ? "شهر" : "3 أشهر"}
          </button>
        ))}
      </div>

      {/* Stats Row */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <StatCard icon="⭐" label="الإجمالي الكلي" value={stats.total_all_time.toLocaleString("ar")} />
        <StatCard icon="📅" label="هذا الأسبوع" value={stats.total_this_week.toLocaleString("ar")} />
        <StatCard icon="☀️" label="اليوم" value={stats.total_today.toLocaleString("ar")} />
      </div>

      {/* Two columns: breakdown + chart */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        {/* Breakdown */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "20px 18px", boxShadow: "0 2px 12px rgba(91,14,20,.08)", border: "1px solid rgba(91,14,20,.08)" }}>
          <h3 style={{ margin: "0 0 16px", color: B, fontSize: ".9rem", fontWeight: 800 }}>التفاصيل (الكلي)</h3>
          {[
            { label: "أسئلة أُضيفت", icon: "✅", key: "questions_added" },
            { label: "أسئلة عُدّلت", icon: "✏️", key: "questions_edited" },
            { label: "أسئلة حُذفت", icon: "🗑️", key: "questions_deleted" },
            { label: "فئات", icon: "🗂️", key: "categories" },
            { label: "وسائط", icon: "🖼️", key: "media" },
            { label: "أخرى", icon: "📋", key: "other" },
          ].map(({ label, icon, key }) => (
            <div key={key} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: ".82rem", color: "#555" }}>{icon} {label}</span>
                <span style={{ fontSize: ".82rem", fontWeight: 800, color: B }}>{breakdown[key] ?? 0}</span>
              </div>
              <MiniBar value={breakdown[key] ?? 0} max={maxBar} />
            </div>
          ))}
        </div>

        {/* 7-day chart */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "20px 18px", boxShadow: "0 2px 12px rgba(91,14,20,.08)", border: "1px solid rgba(91,14,20,.08)" }}>
          <h3 style={{ margin: "0 0 16px", color: B, fontSize: ".9rem", fontWeight: 800 }}>الأيام الـ 7 الأخيرة</h3>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 100 }}>
            {chart.map((c, i) => {
              const barH = maxChart > 0 ? Math.max((c.count / maxChart) * 84, c.count > 0 ? 6 : 0) : 0;
              const isToday = i === chart.length - 1;
              return (
                <div key={c.date} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: ".6rem", color: "#aaa" }}>{c.count > 0 ? c.count : ""}</span>
                  <div style={{ width: "100%", height: barH, background: isToday ? B : "rgba(91,14,20,.25)", borderRadius: "4px 4px 0 0", transition: "height .5s ease" }} />
                  <span style={{ fontSize: ".58rem", color: isToday ? B : "#ccc", fontWeight: isToday ? 800 : 400 }}>
                    {new Date(c.date).toLocaleDateString("ar-SA", { weekday: "short" })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent activity log */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 18px", boxShadow: "0 2px 12px rgba(91,14,20,.08)", border: "1px solid rgba(91,14,20,.08)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: B, fontSize: ".9rem", fontWeight: 800 }}>آخر النشاطات ({days} يوم)</h3>
          <span style={{ fontSize: ".75rem", color: "#aaa" }}>{pagination.total} إجمالي</span>
        </div>

        {logs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "#aaa" }}>لا يوجد نشاط في هذه الفترة</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {logs.map((log, i) => (
              <div key={log.id || i} style={{
                display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 12px",
                borderRadius: 10, background: i % 2 === 0 ? CREAM : "transparent",
              }}>
                <span style={{ fontSize: "1.1rem", marginTop: 1 }}>{actionIcon(log.action)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: ".83rem", fontWeight: 700, color: "#333" }}>
                    {log.action}
                    {log.target_name && <span style={{ fontWeight: 400, color: "#666" }}> — {log.target_name}</span>}
                  </div>
                  {log.details && (
                    <div style={{ fontSize: ".72rem", color: "#999", marginTop: 2 }}>{log.details}</div>
                  )}
                </div>
                <span style={{ fontSize: ".72rem", color: "#aaa", whiteSpace: "nowrap", marginTop: 2 }}>
                  {timeAgo(log.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}

        {pagination.pages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 20 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: "6px 14px", borderRadius: 8, border: "none", cursor: page === 1 ? "default" : "pointer", background: "rgba(91,14,20,.1)", color: B, opacity: page === 1 ? 0.4 : 1 }}>
              السابق
            </button>
            <span style={{ fontSize: ".82rem", color: "#666" }}>{page} / {pagination.pages}</span>
            <button onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages}
              style={{ padding: "6px 14px", borderRadius: 8, border: "none", cursor: page === pagination.pages ? "default" : "pointer", background: "rgba(91,14,20,.1)", color: B, opacity: page === pagination.pages ? 0.4 : 1 }}>
              التالي
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CSS Animations ──────────────────────────────────────────────────────────

const globalStyles = `
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
`;

// ─── Main Export ─────────────────────────────────────────────────────────────

export default function MyActivityPanel({ token, isSuperAdmin }) {
  const [tab, setTab] = useState("questions");
  const [adminName, setAdminName] = useState("");
  const [categories, setCategories] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch admin name + categories on mount
  useEffect(() => {
    // Try localStorage first
    const stored = localStorage.getItem("admin_name");
    if (stored) setAdminName(stored);

    // Fetch from /api/admin/verify for authoritative value
    axios.get(`${API}/api/admin/verify`, { headers })
      .then(res => {
        const name = res.data?.admin_name || res.data?.name || stored || "";
        setAdminName(name);
      })
      .catch(() => {});

    // Fetch categories
    axios.get(`${API}/api/categories`, { headers })
      .then(res => setCategories(res.data || []))
      .catch(() => {});
  }, [token]);

  const tabs = [
    { key: "questions", label: "📝 أسئلتي" },
    { key: "stats",     label: "📈 الإحصائيات" },
    { key: "log",       label: "📋 السجل" },
  ];

  return (
    <>
      <style>{globalStyles}</style>
      <div dir="rtl" style={{ padding: "24px 16px", maxWidth: 920, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ margin: 0, color: B, fontSize: "1.4rem", fontWeight: 900 }}>نشاطي 📊</h2>
          {adminName && (
            <p style={{ margin: "4px 0 0", color: "#888", fontSize: ".85rem" }}>{adminName}</p>
          )}
        </div>

        {/* Tab nav */}
        <div style={{
          display: "flex", gap: 4, marginBottom: 24,
          background: "rgba(91,14,20,.06)", borderRadius: 12, padding: 4,
        }}>
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                flex: 1, padding: "10px 16px", borderRadius: 10, border: "none",
                cursor: "pointer", fontWeight: 700, fontSize: ".85rem", fontFamily: "inherit",
                background: tab === t.key ? "#fff" : "transparent",
                color: tab === t.key ? B : "#6b7280",
                boxShadow: tab === t.key ? "0 2px 8px rgba(91,14,20,.12)" : "none",
                transition: "all .2s ease",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ animation: "slideUp .2s ease" }} key={tab}>
          {tab === "questions" && (
            <QuestionsTab
              token={token}
              adminName={adminName}
              isSuperAdmin={isSuperAdmin}
              categories={categories}
            />
          )}
          {tab === "stats" && <StatsTab token={token} />}
          {tab === "log"   && <LogTab   token={token} />}
        </div>

      </div>
    </>
  );
}
